// Watches the game store and plays the AI sides. Every decision is shown as
// a beat before it lands, and the AI acts only when the board is quiet: no
// stage, no beat playing, nothing queued, no earlier step on the board. So nothing it does can run over
// something the player is still reading.

import { chooseSpellTarget } from '../ai/aim';
import type { CastPlan } from '../ai/castPlan';
import { requestCastPlan, requestSearch } from '../ai/client';
import { type BehindCount, countBehind, givesUp, NO_BEHIND_COUNT } from '../ai/concede';
import { planDepth, planNote, type SearchPlan, searchPlanFor } from '../ai/pitch';
import { paysForRoll } from '../ai/roll';
import {
  AI_CAST_PLAN_BUDGET_MS,
  AI_CONCEDE_PROBE_DEPTH,
  AI_CONCEDE_TURNS,
  AI_READ_DELAY_MS,
  AI_SUGGEST_BUDGET_MS,
  AI_TIME_BUDGET_MS,
  BEAT_CONSIDER_MS,
  BEAT_LIFT_MS,
  BEAT_PICK_MS,
  BEAT_RECAP_MS,
} from '../constants';
import { squareName } from '../engine/squares';
import type { Side } from '../engine/types';
import { slotsOf } from '../game/casters';
import { aimsAtRegion, eligibleTargets, regionOfPick, removedByPick } from '../game/flow';
import type { GameState } from '../game/state';
import { carryMoveMs } from '../motion/carry';
import { describePiece, OWNER_NAMES } from '../spells/helpers';
import { spellById } from '../spells/library';
import { isBoardOpen } from './boardCover';
import { endCarry, liftPiece, releasePiece } from './carry';
import { beat, clearBeat, holdBeat, playBeat, playBeatAndHold, whenQuiet } from './director';
import { aimAt, castSlot, concede, gameStore, matchRng, playMove, rollDie } from './game';
import { isReviewing } from './history';
import { actsFor } from './localSide';
import { describeIntent, moveLine } from './moveBeats';

/** Sides the driver plays. Spectate mode adds gold. */
let aiSides: readonly Side[] = ['violet'];

let busy = false;

/**
 * The spell the AI cast from a slot, with the targets its plan found. The aim
 * takes them. A target that is no pick any more gives way to the choice of
 * the spell.
 */
let aimPlan: { spellId: string; targets: readonly number[] } | null = null;

/** The turns in a row each side the driver plays was far behind. See `ai/concede.ts`. */
const behind: Record<Side, BehindCount> = { gold: NO_BEHIND_COUNT, violet: NO_BEHIND_COUNT };

const wait = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * True while nothing else has changed the store since we took the snapshot
 * and the player still watches the live game. A step back, or the cover of
 * the board for a new match, stops the decision. It starts again from its
 * first beat when the player returns.
 */
const isStillCurrent = (snapshot: GameState): boolean =>
  gameStore.get() === snapshot && !isReviewing() && isBoardOpen();

/** Waits for the stage and every beat to finish, then a moment to read. */
const settle = async (): Promise<void> => {
  await whenQuiet();
  await wait(AI_READ_DELAY_MS);
};

const actOnRoll = async (state: GameState): Promise<void> => {
  await settle();
  if (isStillCurrent(state)) rollDie();
};

const actOnAim = async (state: GameState, actor: string): Promise<void> => {
  if (!state.pendingSpell) return;
  const spell = spellById(state.pendingSpell);
  const targets = eligibleTargets(state);
  await settle();
  if (!isStillCurrent(state)) return;

  // A spell that takes a region has a target on every square. Its targets are the regions,
  // and the weigh beat marks no square.
  const takesRegion = aimsAtRegion(state);
  const targetCount = takesRegion
    ? new Set(targets.map((square) => regionOfPick(state, square)?.middle)).size
    : targets.length;
  const count = `${targetCount} possible target${targetCount === 1 ? '' : 's'}.`;
  await playBeat(
    beat(`${actor} weighs ${spell.name}`, count, {
      marks: takesRegion ? [] : targets.map((square) => ({ square, kind: 'consider' as const })),
      tone: spell.polarity,
      spotlight: !takesRegion,
    }),
    BEAT_CONSIDER_MS,
  );
  if (!isStillCurrent(state)) return;

  const planned =
    aimPlan?.spellId === spell.id ? aimPlan.targets[state.pendingTargets.length] : undefined;
  const target =
    planned !== undefined && targets.includes(planned)
      ? planned
      : chooseSpellTarget(state, targets, spell, state.turn, matchRng());
  if (target === null) return;
  const region = regionOfPick(state, target);
  if (region) {
    await playBeat(
      beat(`${actor} picks the squares around ${squareName(region.middle)}`, spell.blurb, {
        marks: region.squares.map((square) => ({ square, kind: 'consider' as const })),
        tone: spell.polarity,
        spotlight: true,
      }),
      BEAT_PICK_MS,
    );
    if (!isStillCurrent(state)) return;
    // The middle takes the same region, and the spark of the cast leaves from it.
    aimAt(region.middle);
    return;
  }
  const piece = state.board[target];
  const what = piece ? describePiece(piece, target) : squareName(target);
  await playBeat(
    beat(`${actor} picks ${what}`, spell.blurb, {
      // A pick that removes pieces shows each one with a cross, as the hover does for the player.
      marks: [
        { square: target, kind: 'pick' },
        ...removedByPick(state, target).map((square) => ({ square, kind: 'remove' as const })),
      ],
      tone: spell.polarity,
      spotlight: true,
    }),
    BEAT_PICK_MS,
  );
  if (!isStillCurrent(state)) return;

  aimAt(target);
};

/**
 * Godsmode: true when the side gives up at this move. `score` is what its
 * search for the move gave. A side with less than the full search has a
 * score that is too shallow, or in the units of the aggro pitch, so it looks
 * again with the usual score. The count is not in the match record: a
 * reload starts it from zero.
 */
const givesUpNow = async (state: GameState, plan: SearchPlan, score: number): Promise<boolean> => {
  if (state.mode !== 'gods') return false;
  const side = state.turn;
  const probed =
    plan.strength === 'full'
      ? score
      : (
          await requestSearch({
            board: state.board,
            rules: state.rules,
            side,
            onlyPieceId: state.mustMovePieceId,
            maxDepth: AI_CONCEDE_PROBE_DEPTH,
            budgetMs: AI_TIME_BUDGET_MS,
          })
        ).score;
  behind[side] = countBehind(behind[side], state.seed, state.ply, probed);
  return givesUp(behind[side]);
};

/**
 * Looks for the move. With energy for a roll and no good move, it rolls and
 * comes back. With `mayRoll` false it only moves: in Godsmode the one roll of
 * a turn gives the energy, and the casts come before this. With `settled`
 * true the caller waited for the board already.
 */
const actOnMove = async (
  state: GameState,
  actor: string,
  mayRoll = true,
  settled = false,
): Promise<void> => {
  if (!settled) await settle();
  if (!isStillCurrent(state)) return;

  // The pitches of the side say how far and how it looks. The beat says what it lacks.
  const searchPlan = searchPlanFor(state);
  const depth = planDepth(searchPlan);
  const thinking =
    state.mustMovePieceId !== null
      ? 'Double Time. The same piece moves again.'
      : planNote(searchPlan);
  holdBeat(beat(`${actor} is thinking`, thinking));
  const { move, score } = await requestSearch({
    board: state.board,
    rules: state.rules,
    side: state.turn,
    onlyPieceId: state.mustMovePieceId,
    style: searchPlan.style,
    ...(depth === undefined ? {} : { maxDepth: depth }),
    // With suggest-best the AI looks deeper, as the suggestion for the player does.
    budgetMs: state.suggestFor === state.turn ? AI_SUGGEST_BUDGET_MS : AI_TIME_BUDGET_MS,
  });
  if (!isStillCurrent(state) || !move) {
    clearBeat();
    return;
  }
  if (await givesUpNow(state, searchPlan, score)) {
    await playBeat(
      beat(`${actor} gives up`, `It was far behind on ${AI_CONCEDE_TURNS} turns in a row.`),
      BEAT_RECAP_MS,
    );
    if (isStillCurrent(state)) concede(state.turn);
    return;
  }
  if (!isStillCurrent(state)) {
    clearBeat();
    return;
  }
  // The search comes before the roll: with a good move the AI keeps its energy.
  if (mayRoll && paysForRoll(state, score)) {
    clearBeat();
    rollDie();
    return;
  }

  // The piece comes up and hangs over its square, then travels as the move is
  // played. Each beat holds until the next one, so a beat for an event of the
  // move waits for the recap.
  const intent = describeIntent(state, move);
  const mover = state.board[move.from];
  const plan = mover ? liftPiece(mover.id, move.from, move.to) : null;
  await playBeatAndHold(intent, BEAT_LIFT_MS);
  if (!isStillCurrent(state)) {
    endCarry();
    clearBeat();
    return;
  }

  releasePiece();
  playMove(move);
  const after = gameStore.get();
  if (plan) await playBeatAndHold({ ...intent, marks: [] }, carryMoveMs(plan));
  endCarry();
  await playBeat(beat(`${actor} moved`, moveLine(state, after)), BEAT_RECAP_MS);
};

/**
 * Godsmode: the AI casts from a slot when its plan finds a cast worth making.
 * Then its pieces move. The plan draws from a source of its own, from the
 * state of the match source, so it takes nothing from the match.
 */
const actOnCasterMove = async (state: GameState, actor: string): Promise<void> => {
  aimPlan = null;
  await settle();
  if (!isStillCurrent(state)) return;
  const plan: CastPlan | null = await requestCastPlan({
    state,
    sampleSeed: matchRng().state(),
    budgetMs: AI_CAST_PLAN_BUDGET_MS,
  });
  if (!isStillCurrent(state)) return;
  if (plan === null) {
    await actOnMove(state, actor, false, true);
    return;
  }
  const slot = plan.slot;
  // A cast from a slot has no die and no card. This beat says what the AI casts, and
  // that a scroll is used up, before the spell lands.
  const held = slotsOf(state, state.turn)[slot];
  const spell = held?.spellId ? spellById(held.spellId) : null;
  if (held && spell) {
    const price = held.kind === 'scroll' ? 'It uses a scroll.' : `It pays ${held.cost} energy.`;
    await playBeat(
      beat(`${actor} casts ${spell.name}`, price, { tone: spell.polarity }),
      BEAT_PICK_MS,
    );
    if (!isStillCurrent(state)) return;
    aimPlan = { spellId: spell.id, targets: plan.targets };
  }
  castSlot(slot);
};

const act = async (state: GameState): Promise<void> => {
  busy = true;
  const actor = OWNER_NAMES[state.turn];
  try {
    // A roll the AI is owed. A roll for energy is a choice, and the move makes it.
    if (state.phase === 'roll') await actOnRoll(state);
    else if (state.phase === 'aim') await actOnAim(state, actor);
    else if (state.phase === 'move' && state.mode === 'gods') await actOnCasterMove(state, actor);
    else if (state.phase === 'move') await actOnMove(state, actor);
  } finally {
    busy = false;
    // The store may have changed while we worked. Look again.
    queueMicrotask(() => consider(gameStore.get()));
  }
};

/**
 * Godsmode: the roll for energy at the start of a turn of the player. The
 * driver makes it when the board is quiet, with no click. Rob, 2026-10-13:
 * "Let's make the cast for energy automatic to start the turn.... it's fun to
 * press the button but it would all feel much faster if it just happened."
 * The roll is the same action as a click on the die, so the record is the
 * same, and the die is still a button until this runs.
 */
const rollForPlayer = async (state: GameState): Promise<void> => {
  busy = true;
  try {
    await whenQuiet();
    if (isStillCurrent(state)) rollDie();
  } finally {
    busy = false;
    queueMicrotask(() => consider(gameStore.get()));
  }
};

const consider = (state: GameState): void => {
  if (busy || state.phase === 'over') return;
  if (aiSides.includes(state.turn)) void act(state);
  else if (state.mode === 'gods' && state.phase === 'roll' && actsFor(state.turn)) {
    void rollForPlayer(state);
  }
};

/** Call once at startup. */
export const startAiDriver = (sides: readonly Side[]): void => {
  aiSides = sides;
  gameStore.subscribe(consider);
  consider(gameStore.get());
};

/** A room has no AI side. Leaving the room gives the AI its side again. */
export const setAiSides = (sides: readonly Side[]): void => {
  aiSides = sides;
  consider(gameStore.get());
};
